import React, { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import {
  ArrowLeft, ArrowUpRight, BookOpen, Brain, ExternalLink, GraduationCap, Leaf,
  Loader2, Search, Sparkles, TreePine, Trophy, Users, X,
} from 'lucide-react';
import { apiUrl } from '../api';
import MarkdownLite from './MarkdownLite';
import EducationGames from './EducationGames';
import PublicToolNav from './PublicToolNav';

interface KnowledgeEntry {
  id: string;
  title: string;
  content: string;
  category: string;
  categoryLabel?: string;
  authoritativeSource?: string;
  sourceUrl?: string;
  lastUpdated?: string;
  kind?: 'entry' | 'alert' | 'meta';
  section?: string;
  order?: number;
  entryCount?: number;
  excerpt?: string;
  isArchived?: boolean;
}

interface Props {
  publicMode?: boolean;
  onBack?: () => void;
  onNavigate?: (view: string) => void;
}

const AUTHORITATIVE_GROUPS = [
  {
    label: 'Professional organizations',
    links: [
      { name: 'International Society of Arboriculture', url: 'https://www.isa-arbor.com', note: 'Credentialing, Arborist News, AUF journal' },
      { name: 'ISA Southern Chapter', url: 'https://www.isasouthern.org', note: 'Regional conferences and CEUs' },
      { name: 'Tree Care Industry Association', url: 'https://treecareindustryassociation.org', note: 'TCI EXPO, safety, business resources' },
      { name: 'American Society of Consulting Arborists', url: 'https://www.asca-consultants.org', note: 'Consulting arborist standards' },
      { name: 'Georgia Arborist Association', url: 'https://www.georgiaarborist.org', note: 'Georgia events, TCC, credentials' },
    ],
  },
  {
    label: 'Safety & standards',
    links: [
      { name: 'ANSI Z133', url: 'https://www.isa-arbor.com/store/product/1266', note: 'Arboricultural operations safety' },
      { name: 'ANSI A300', url: 'https://www.isa-arbor.com/store', note: 'Tree care operations standards' },
      { name: 'OSHA Tree Care', url: 'https://www.osha.gov/tree-care', note: 'Regulations and rulemaking' },
      { name: 'CPSC Recalls', url: 'https://www.cpsc.gov/Recalls', note: 'Product recall alerts' },
      { name: 'Call 811', url: 'https://call811.com', note: 'Utility locate before digging' },
    ],
  },
  {
    label: 'Research & extension',
    links: [
      { name: 'USDA Forest Service — Urban Forestry', url: 'https://www.fs.usda.gov/managing-land/urban-forests/ucf', note: 'Federal urban forestry programs' },
      { name: 'UGA Warnell School', url: 'https://warnell.uga.edu', note: 'Georgia forestry research' },
      { name: 'UGA Extension', url: 'https://extension.uga.edu', note: 'Local diagnostics and publications' },
      { name: 'Georgia Forestry Commission', url: 'https://gatrees.org', note: 'State forestry and BMPs' },
      { name: 'Bartlett Tree Research Laboratories', url: 'https://www.bartlett.com/research', note: 'Applied tree research' },
      { name: 'TREE Fund', url: 'https://treefund.org', note: 'Research grants' },
      { name: 'i-Tree Tools', url: 'https://www.itreetools.org', note: 'Free urban forest analysis tools' },
    ],
  },
  {
    label: 'Georgia & local',
    links: [
      { name: 'Georgia Department of Agriculture', url: 'https://agr.georgia.gov', note: 'Pesticide licensing and pest alerts' },
      { name: 'Clemson Regulatory Services', url: 'https://blogs.clemson.edu/regulatory/', note: 'Southeast invasive pest updates' },
      { name: 'Columbus Planning', url: 'https://columbusga.gov/planning', note: 'Zoning and development' },
      { name: 'Columbus/Muscogee GIS', url: 'https://ccggisprod.columbusga.org/server/rest/services/TaxAssessors/Base/MapServer', note: 'Parcel and tax GIS' },
      { name: 'Phenix City', url: 'https://www.phenixcityal.us', note: 'Local codes and public works' },
    ],
  },
  {
    label: 'Learning & community',
    links: [
      { name: 'Arbor Day Foundation', url: 'https://www.arborday.org', note: 'Tree planting and education' },
      { name: 'Morton Arboretum', url: 'https://mortonarb.org', note: 'Tree science and learning' },
      { name: 'TCIA Safety', url: 'https://treecareindustryassociation.org/safety', note: 'Crew safety resources' },
      { name: 'OpenDroneMap', url: 'https://www.opendronemap.org', note: 'Open-source photogrammetry' },
      { name: 'USGS Lidar Explorer', url: 'https://apps.nationalmap.gov/lidar-explorer/', note: 'Free lidar coverage' },
      { name: 'FEMA Flood Maps', url: 'https://msc.fema.gov', note: 'Flood zone lookup' },
    ],
  },
];

const CATEGORY_LABELS: Record<string, string> = {
  alerts: 'Industry watch',
  organizations: 'Organizations',
  safety: 'Safety & standards',
  business: 'Business',
  research: 'Research & extension',
  technology: 'Technology',
  training: 'Training & CEUs',
  georgia: 'Georgia',
  publications: 'Publications',
  careers: 'Jobs & careers',
  grants: 'Grants',
  general: 'General',
};

function formatDate(value?: string) {
  if (!value) return '—';
  const date = new Date(value);
  return isNaN(date.getTime()) ? '—' : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const EducationView: React.FC<Props> = ({ publicMode = false, onBack, onNavigate }) => {
  const [entries, setEntries] = useState<KnowledgeEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState<'learn' | 'library' | 'links' | 'games'>('learn');
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [selected, setSelected] = useState<KnowledgeEntry | null>(null);

  useEffect(() => {
    if (!publicMode) return;
    const previousTitle = document.title;
    document.title = 'Education Center — Tree Care Guides, Industry News & Games | GreenTree';
    const setMeta = (selector: string, attr: 'name' | 'property', key: string, value: string) => {
      let el = document.head.querySelector(selector) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute('content', value);
    };
    setMeta('meta[name="description"]', 'name', 'description', 'GreenTree Arboriculture education: current tree care industry updates, safety standards, Georgia-specific guidance, authoritative links, and learning games for kids and adults.');
    setMeta('meta[property="og:title"]', 'property', 'og:title', 'GreenTree Education Center');
    setMeta('meta[property="og:description"]', 'property', 'og:description', 'Tree care guides, industry watch, trusted resources, and learning games from GreenTree Arboriculture.');
    let canonical = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', 'https://greentreearboriculture.com/education');
    return () => { document.title = previousTitle; };
  }, [publicMode]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const response = await fetch(apiUrl('/api/education'));
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        if (!cancelled) {
          setEntries(Array.isArray(data.entries) ? data.entries : []);
          setLoading(false);
          setError('');
        }
      } catch {
        if (!cancelled) {
          setLoading(false);
          setError('The education library is temporarily unavailable. Please try again shortly.');
        }
      }
    };
    load();
    const timer = window.setInterval(load, 5 * 60 * 1000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, []);

  const meta = entries.find(entry => entry.kind === 'meta');
  const alert = entries.find(entry => entry.kind === 'alert');
  const guides = useMemo(
    () => entries.filter(entry => entry.kind !== 'meta' && entry.kind !== 'alert')
      .sort((a, b) => (a.order || 0) - (b.order || 0) || a.title.localeCompare(b.title)),
    [entries],
  );
  const lastUpdated = meta?.lastUpdated || guides[0]?.lastUpdated;
  const categories = useMemo(() => {
    const map = new Map<string, string>();
    guides.forEach(guide => {
      if (guide.category) map.set(guide.category, guide.categoryLabel || CATEGORY_LABELS[guide.category] || guide.category);
    });
    return Array.from(map.entries());
  }, [guides]);
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return guides.filter(guide => {
      if (category !== 'all' && guide.category !== category) return false;
      if (!term) return true;
      return `${guide.title} ${guide.content} ${guide.categoryLabel || ''}`.toLowerCase().includes(term);
    });
  }, [guides, search, category]);

  const openEntry = (entry: KnowledgeEntry) => setSelected(entry);

  if (publicMode) {
    return (
      <div className="min-h-screen bg-stone-50">
        <header className="bg-bark text-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-5 flex items-center justify-between gap-3">
            <button onClick={onBack} className="inline-flex items-center gap-2 text-sm font-bold text-stone-300 hover:text-white transition">
              <ArrowLeft className="w-4 h-4" /> Back to GreenTree
            </button>
            <span className="text-[10px] font-black uppercase tracking-[0.18em] text-gecko">Education Center</span>
          </div>
        </header>
        <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
          <EducationBody
            entries={entries}
            guides={guides}
            filtered={filtered}
            categories={categories}
            alert={alert}
            lastUpdated={lastUpdated}
            loading={loading}
            error={error}
            tab={tab}
            setTab={setTab}
            search={search}
            setSearch={setSearch}
            category={category}
            setCategory={setCategory}
            openEntry={openEntry}
          />
        </main>
        {onNavigate && <PublicToolNav active="education" onNavigate={onNavigate} />}
        <EntryModal entry={selected} onClose={() => setSelected(null)} />
      </div>
    );
  }

  return (
    <div>
      <EducationBody
        entries={entries}
        guides={guides}
        filtered={filtered}
        categories={categories}
        alert={alert}
        lastUpdated={lastUpdated}
        loading={loading}
        error={error}
        tab={tab}
        setTab={setTab}
        search={search}
        setSearch={setSearch}
        category={category}
        setCategory={setCategory}
        openEntry={openEntry}
      />
      <EntryModal entry={selected} onClose={() => setSelected(null)} />
    </div>
  );
};

interface BodyProps {
  entries: KnowledgeEntry[];
  guides: KnowledgeEntry[];
  filtered: KnowledgeEntry[];
  categories: Array<[string, string]>;
  alert?: KnowledgeEntry;
  lastUpdated?: string;
  loading: boolean;
  error: string;
  tab: 'learn' | 'library' | 'links' | 'games';
  setTab: (tab: 'learn' | 'library' | 'links' | 'games') => void;
  search: string;
  setSearch: (value: string) => void;
  category: string;
  setCategory: (value: string) => void;
  openEntry: (entry: KnowledgeEntry) => void;
}

const EducationBody: React.FC<BodyProps> = ({
  entries, guides, filtered, categories, alert, lastUpdated, loading, error,
  tab, setTab, search, setSearch, category, setCategory, openEntry,
}) => {
  const featured = guides.slice(0, 6);

  return (
    <div className="space-y-7">
      <section className="bg-bark text-white rounded-[32px] p-6 sm:p-9 relative overflow-hidden">
        <div className="absolute -right-10 -top-10 w-56 h-56 rounded-full border-[22px] border-white/10" />
        <div className="relative z-10 max-w-3xl">
          <p className="text-[10px] font-black uppercase tracking-[0.18em] text-gecko flex items-center gap-1.5">
            <GraduationCap className="w-4 h-4" /> GreenTree Learning Center
          </p>
          <h1 className="text-3xl sm:text-4xl font-black mt-3 leading-tight">Tree care knowledge, kept current.</h1>
          <p className="text-sm text-stone-300 mt-3 max-w-2xl leading-relaxed">
            Industry updates, safety standards, Georgia-specific guidance, and free field resources —
            for homeowners, crews, and curious kids.
          </p>
          <div className="flex flex-wrap gap-2 mt-5">
            <span className="px-3 py-1.5 rounded-full bg-white/10 text-[10px] font-bold text-stone-200">
              Last updated {formatDate(lastUpdated)}
            </span>
            <span className="px-3 py-1.5 rounded-full bg-white/10 text-[10px] font-bold text-stone-200">
              {guides.length} guides
            </span>
            <span className="px-3 py-1.5 rounded-full bg-white/10 text-[10px] font-bold text-stone-200">
              {AUTHORITATIVE_GROUPS.reduce((sum, group) => sum + group.links.length, 0)} trusted links
            </span>
          </div>
        </div>
      </section>

      <div className="flex flex-wrap gap-2">
        {([
          ['learn', 'Learn', BookOpen],
          ['library', 'Knowledge library', Search],
          ['links', 'Trusted links', ExternalLink],
          ['games', 'Games', Brain],
        ] as const).map(([id, label, Icon]) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition ${
              tab === id ? 'bg-bark text-white' : 'bg-white border border-stone-200 text-stone-500 hover:bg-stone-50'
            }`}
          >
            <Icon className="w-3.5 h-3.5" /> {label}
          </button>
        ))}
      </div>

      {loading && (
        <div className="bg-white rounded-[28px] border border-stone-200 p-10 text-center">
          <Loader2 className="w-6 h-6 text-gecko animate-spin mx-auto" />
          <p className="mt-3 text-xs font-black uppercase tracking-widest text-stone-400">Loading the field library…</p>
        </div>
      )}
      {!loading && error && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-800">{error}</div>
      )}

      {!loading && !error && tab === 'learn' && (
        <div className="space-y-6">
          {alert && (
            <button onClick={() => openEntry(alert)} className="w-full text-left bg-white rounded-[28px] border border-amber-200 p-5 sm:p-6 hover:shadow-md transition">
              <p className="text-[10px] font-black uppercase tracking-[0.16em] text-amber-700 flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5" /> Industry watch · {formatDate(alert.lastUpdated)}
              </p>
              <h2 className="text-lg font-black text-bark mt-2">{alert.title}</h2>
              <p className="text-xs sm:text-sm text-stone-500 mt-2 line-clamp-3">{alert.excerpt || alert.content.slice(0, 240)}</p>
              <span className="inline-flex items-center gap-1.5 mt-3 text-[10px] font-black uppercase tracking-widest text-palm">
                Read the latest <ArrowUpRight className="w-3.5 h-3.5" />
              </span>
            </button>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {[
              { icon: Leaf, title: 'For homeowners', text: 'Tree health, risk, planting, and when to call a pro.', filter: 'research' },
              { icon: Users, title: 'For crews', text: 'Safety standards, gear, rigging, and field references.', filter: 'safety' },
              { icon: TreePine, title: 'For kids', text: 'Leaf Match, tree ID, and safe choices around trees.', filter: 'games' },
            ].map(card => (
              <button
                key={card.title}
                onClick={() => card.filter === 'games' ? setTab('games') : (setCategory(card.filter), setTab('library'))}
                className="text-left bg-white rounded-2xl border border-stone-200 p-5 hover:border-gecko/40 hover:shadow-sm transition"
              >
                <span className="w-9 h-9 rounded-xl bg-leaf/15 text-palm flex items-center justify-center"><card.icon className="w-[18px] h-[18px]" /></span>
                <p className="text-sm font-black text-bark mt-3">{card.title}</p>
                <p className="text-[11px] text-stone-400 mt-1 leading-relaxed">{card.text}</p>
              </button>
            ))}
          </div>

          <div>
            <div className="flex items-end justify-between gap-3 mb-3">
              <div>
                <p className="text-[10px] font-black uppercase tracking-[0.16em] text-stone-400">Start here</p>
                <h2 className="text-lg font-black text-bark mt-1">Featured field guides</h2>
              </div>
              <button onClick={() => setTab('library')} className="text-[10px] font-black uppercase tracking-widest text-palm hover:underline">View all</button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
              {featured.map(entry => (
                <GuideCard key={entry.id} entry={entry} onClick={() => openEntry(entry)} />
              ))}
            </div>
          </div>
        </div>
      )}

      {!loading && !error && tab === 'library' && (
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="relative flex-1">
              <Search className="w-4 h-4 text-stone-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search the field library"
                className="w-full pl-10 pr-4 py-3 rounded-xl bg-white border border-stone-200 text-sm font-medium text-bark outline-none focus:ring-2 focus:ring-gecko/30"
              />
            </div>
            <select
              value={category}
              onChange={e => setCategory(e.target.value)}
              className="px-4 py-3 rounded-xl bg-white border border-stone-200 text-xs font-bold text-bark outline-none"
            >
              <option value="all">All categories ({guides.length})</option>
              {categories.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
            </select>
          </div>
          <p className="text-[10px] font-black uppercase tracking-widest text-stone-400">
            {filtered.length} result{filtered.length === 1 ? '' : 's'}
          </p>
          {filtered.length === 0 ? (
            <div className="bg-white rounded-[28px] border border-stone-200 p-10 text-center">
              <Search className="w-8 h-8 text-stone-300 mx-auto" />
              <p className="mt-3 text-sm font-black text-stone-500">No guides match that search</p>
              <p className="text-xs text-stone-400 mt-1">Try a broader term or choose another category.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
              {filtered.map(entry => <GuideCard key={entry.id} entry={entry} onClick={() => openEntry(entry)} />)}
            </div>
          )}
        </div>
      )}

      {!loading && !error && tab === 'links' && (
        <div className="space-y-6">
          {AUTHORITATIVE_GROUPS.map(group => (
            <div key={group.label}>
              <h2 className="text-sm font-black text-bark mb-2">{group.label}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2">
                {group.links.map(link => (
                  <a
                    key={link.name}
                    href={link.url}
                    target="_blank"
                    rel="noreferrer"
                    className="group bg-white rounded-2xl border border-stone-200 p-4 hover:border-gecko/40 hover:shadow-sm transition"
                  >
                    <span className="flex items-start justify-between gap-2">
                      <span className="text-xs font-black text-bark">{link.name}</span>
                      <ExternalLink className="w-3.5 h-3.5 text-stone-300 group-hover:text-palm shrink-0" />
                    </span>
                    <span className="block text-[10px] text-stone-400 mt-1 leading-relaxed">{link.note}</span>
                  </a>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && !error && tab === 'games' && (
        <div className="space-y-4">
          <div className="bg-white rounded-[28px] border border-stone-200 p-5 sm:p-6">
            <p className="text-[10px] font-black uppercase tracking-[0.16em] text-stone-400">Learning games</p>
            <h2 className="text-xl font-black text-bark mt-1">Learn by playing</h2>
            <p className="text-sm text-stone-500 mt-1 max-w-2xl">A memory game for kids, a tree ID challenge for all ages, and a hazard-decision game for crews and homeowners.</p>
          </div>
          <EducationGames />
        </div>
      )}
    </div>
  );
};

const GuideCard: React.FC<{ entry: KnowledgeEntry; onClick: () => void }> = ({ entry, onClick }) => (
  <button onClick={onClick} className="text-left bg-white rounded-2xl border border-stone-200 p-4 hover:border-gecko/40 hover:shadow-sm transition flex flex-col">
    <span className="text-[9px] font-black uppercase tracking-widest text-gecko">
      {entry.categoryLabel || CATEGORY_LABELS[entry.category] || entry.category}
    </span>
    <span className="text-sm font-black text-bark mt-2 leading-snug">{entry.title}</span>
    <span className="text-[11px] text-stone-400 mt-2 leading-relaxed line-clamp-3">
      {entry.excerpt || entry.content.replace(/[#*_>`]/g, '').slice(0, 180)}
    </span>
    <span className="mt-auto pt-3 inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-widest text-palm">
      Read guide <ArrowUpRight className="w-3 h-3" />
    </span>
  </button>
);

const EntryModal: React.FC<{ entry: KnowledgeEntry | null; onClose: () => void }> = ({ entry, onClose }) => (
  <AnimatePresence>
    {entry && (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[80] bg-stone-950/55 backdrop-blur-sm p-3 sm:p-6 flex items-center justify-center"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, y: 18, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 12, scale: 0.98 }}
          onClick={e => e.stopPropagation()}
          className="bg-white w-full max-w-4xl max-h-[88vh] rounded-[28px] shadow-2xl overflow-hidden flex flex-col"
        >
          <div className="p-5 sm:p-6 border-b border-stone-100 flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="text-[10px] font-black uppercase tracking-widest text-gecko">
                {entry.categoryLabel || CATEGORY_LABELS[entry.category] || entry.category}
              </p>
              <h2 className="text-xl sm:text-2xl font-black text-bark mt-1 leading-tight">{entry.title}</h2>
              <p className="text-[10px] text-stone-400 mt-1">Updated {formatDate(entry.lastUpdated)}</p>
            </div>
            <button onClick={onClose} aria-label="Close guide" className="p-2 rounded-xl bg-stone-100 text-stone-500 hover:bg-stone-200 shrink-0">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="p-5 sm:p-7 overflow-y-auto custom-scrollbar">
            <MarkdownLite content={entry.content} />
            {entry.sourceUrl && (
              <a href={entry.sourceUrl} target="_blank" rel="noreferrer" className="mt-6 inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-gecko text-white text-[10px] font-black uppercase tracking-widest">
                Open original source <ExternalLink className="w-3.5 h-3.5" />
              </a>
            )}
            <p className="mt-5 text-[10px] text-stone-400">
              Source: {entry.authoritativeSource || 'GreenTree Arboriculture compendium'}
            </p>
          </div>
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);

export default EducationView;
